import { ChevronDown16, ChevronUp16 } from '@carbon/icons-react';
import * as Select from '@radix-ui/react-select';
import { sortReleases } from '@upright/common/model/releases';
import { Release } from '@upright/common/model/releases/types';
import { useGetAvailableReleases } from '@upright/ui/api/model/releases';
import CheckIcon from '@upright/ui/common/generic/components/icons/hero-icons/hi-check.svg';
import { getTextStyle } from '@upright/ui/common/generic/content/texts/bou-text/text-style';
import { BouColor } from '@upright/ui/common/generic/global/bou-colors';
import { useGetDefaultVersionOfRelease } from '@upright/ui/common/upright/model/output/releases/hooks';
import { friendlyReleaseLabel } from '@upright/ui/common/upright/model/output/releases/options';
import { setModelVersionAndRelease } from '@upright/ui/untidy/redux/actions/actions';
import { getVersionAndRelease } from '@upright/ui/untidy/redux/selectors';
import { useDispatch, useSelector } from 'react-redux';
import styled, { css } from 'styled-components';
import { usePushGtmEvent } from '../../../../google-tag-manager/use-push-gtm-event';
import { CustomGtmEvent } from '../../../../google-tag-manager/use-push-gtm-event/payloads';
import { navAnimateScaleIn, navAnimateScaleOut, navBorderStyles, navFastTransition, navItemStyles } from '../shared';
import { ReleaseWarning } from './release-warning';

export const HeaderReleaseSelector = () => {
  const dispatch = useDispatch();
  const getVersionOfRelease = useGetDefaultVersionOfRelease();
  const { release } = useSelector(getVersionAndRelease);
  const { error, isLoading, data: values } = useGetSelectorValues();
  const { data: availableReleases } = useGetAvailableReleases();
  const pushGtmEvent = usePushGtmEvent();

  if (error || isLoading || !values || !availableReleases) {
    return <CurrentSelection />;
  }
  if (values.length === 1) {
    return <CurrentSelection />;
  }

  const firstValue = values[0];

  const selectedRelease = getReleaseByNumber(availableReleases, release);

  return (
    <Root>
      <>
        <ReleaseWarning releases={availableReleases} selectedRelease={selectedRelease} />
        <Select.Root
          onValueChange={(releaseNumber: string) => {
            const value = getReleaseByNumber(availableReleases, releaseNumber);
            if (!value) {
              return;
            }
            const newRelease = value.number;
            const newVersion = getVersionOfRelease(newRelease);
            if (newVersion) {
              dispatch(setModelVersionAndRelease(newVersion, newRelease));
            }
            pushGtmEvent({
              event: CustomGtmEvent.RELEASE_CHANGE,
              data: {
                selectedRelease: newRelease,
              },
            });
          }}
          value={release ?? unavailableRelease}
          onOpenChange={open => {
            if (open) {
              pushGtmEvent({
                event: CustomGtmEvent.RELEASE_SELECTOR_OPEN,
              });
            }
          }}
        >
          <StyledTrigger>
            <span>Release:</span>
            <Select.Value placeholder={'foobar'} />
            <Select.Icon asChild>
              <>
                <ChevronDown16 />
              </>
            </Select.Icon>
          </StyledTrigger>
          <StyledContent>
            {firstValue === unavailableRelease ||
              (release !== firstValue.number && (
                <StyledScrollUpButton>
                  <ChevronUp16 />
                </StyledScrollUpButton>
              ))}
            <StyledViewPort>
              <Select.Group>
                {values.map(value => {
                  const option = selectorValueToOption(value);
                  return (
                    <StyledItem key={option.value} value={option.value}>
                      <StyledItemIndicator>
                        <CheckIcon />
                      </StyledItemIndicator>
                      <Select.ItemText>{option.label}</Select.ItemText>
                    </StyledItem>
                  );
                })}
              </Select.Group>
            </StyledViewPort>
            <StyledScrollDownButton>
              <ChevronDown16 />
            </StyledScrollDownButton>
          </StyledContent>
        </Select.Root>
      </>
    </Root>
  );
};

function CurrentSelection() {
  const { release, version } = useSelector(getVersionAndRelease);
  return release ? (
    <Root>
      <span>Release: {release}</span>
    </Root>
  ) : (
    <Root>
      <span>Version: {version}</span>
    </Root>
  );
}

function useGetSelectorValues() {
  const { release } = useSelector(getVersionAndRelease);
  return useGetAvailableReleases(releases => {
    const sorted = sortReleases(releases);
    return release === null ? [unavailableRelease, ...sorted] : sorted;
  });
}

function getReleaseByNumber(releases: Release[], selectedReleaseNumber: string | null) {
  if (selectedReleaseNumber === null) {
    return null;
  }
  return releases.find(release => release.number === selectedReleaseNumber);
}

function selectorValueToOption(value: Release | typeof unavailableRelease) {
  return value === unavailableRelease
    ? {
        value: unavailableRelease,
        label: 'Unavailable',
      }
    : {
        value: value.number,
        label: friendlyReleaseLabel(value),
      };
}

const unavailableRelease = 'unavailable' as const;

const Root = styled.div`
  display: flex;
  align-items: center;
  > span {
    color: ${BouColor.WHITE};
    font-size: 12px;
    font-weight: 700;
    white-space: nowrap;
  }
  &:hover,
  &:active {
    > span {
      color: ${BouColor.WHITE};
    }
  }
`;

const StyledTrigger = styled(Select.Trigger)`
  ${navItemStyles};
  ${getTextStyle({ size: 'xxxs' })}
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  white-space: nowrap;
  &:active {
    > span,
    svg {
      color: ${BouColor.WHITE};
    }
  }
  &[data-state='open'] {
    > svg {
      transition: transform ${navFastTransition};
      transform: rotate(180deg);
    }
  }
`;

const StyledContent = styled(Select.Content)`
  background-color: ${BouColor.PLATFORM_BG_GRAY_DARK};
  ${navBorderStyles}
  position: absolute; // Needed to render the content below the trigger and not on top of it. Radix will provide custom options for this later.
  right: 0;
  top: 30px;
  &[data-state='open'] {
    ${navAnimateScaleIn}
  }
  &[data-state='closed'] {
    ${navAnimateScaleOut}
  }
`;

const StyledViewPort = styled(Select.Viewport)`
  min-width: 160px;
  max-height: 200px;
`;

const StyledItem = styled(Select.Item)`
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  color: ${BouColor.PLATFORM_TEXT_WHITE};
  display: flex;
  align-items: center;
  height: 28px;
  padding-left: 25px;
  outline: none;
  &:first-child {
    border-radius: 6px 6px 0 0;
  }
  &[data-disabled] {
    color: ${BouColor.PLATFORM_DISABLED_GRAY};
    cursor: none;
  }

  &[data-highlighted] {
    background-color: ${BouColor.PLATFORM_BG_GRAY};
    * {
      color: ${BouColor.PLATFORM_TEXT_WHITE};
    }
  }
`;

const StyledItemIndicator = styled(Select.ItemIndicator)`
  position: absolute;
  left: 0;
  width: 25px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  svg {
    height: 14px;
  }
`;

const scrollButtonStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 25px;
  background-color: ${BouColor.WHITE};
  border-radius: 6px;
  color: ${BouColor.BLACK};
  cursor: default;
`;

const StyledScrollUpButton = styled(Select.ScrollUpButton)`
  ${scrollButtonStyles};
`;
const StyledScrollDownButton = styled(Select.ScrollDownButton)`
  ${scrollButtonStyles};
`;
